import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  glossaryService,
  entityService,
  entityTypeService,
  attributeService,
  attributeValueService,
  referenceService,
} from '../services';
import {
  AttributeSensitivity,
  Role,
  Asset,
  formatData,
  handleError,
  getObjectNameFromSelection,
  resolveGlossaryAssetSubmit,
  fetchGlossaryAssetRows,
  fetchAttributeValues,
  searchAttributeValues,
  withValuePaths,
  canHaveAttributeValues,
  toFlatAssetRows,
  getStoredAssetListMode,
  ASSET_LIST_MODE,
  ASSET_LIST_MODE_KEY,
} from '../helpers';
import {
  Changelog,
  DataTable,
  MainColumn,
  ObjectDetail,
  ObjectDetails,
  Tabs,
  PrincipalLink,
} from '../components';
import {
  useCustomProperties,
  useQueryParams,
  useSideDialog,
  useModalDialog,
  useGlobalState,
  usePrincipal,
  useNotFound,
  useMessage,
  useStatus,
} from '../hooks';

export const Glossary = () => {
  const { queryParams } = useQueryParams();

  const [glossary, setGlossary] = useState([]);
  const [entities, setEntities] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [selectedEntityTypes, setSelectedEntityTypes] = useState([]);
  const [loadingEntityTypesTable, setLoadingEntityTypesTable] = useState(true);
  const [fromReferences, setFromReferences] = useState([]);
  const [enableEdit, setEnableEdit] = useState(true);
  const [loggedInUser] = useGlobalState('loggedInUser');
  const [statuses] = useGlobalState('statuses');
  const [settings] = useGlobalState('settings');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  // The values of the attributes that have been expanded, held apart from the tree rows so that
  // reloading the tree — which every save does — does not empty the open expansions. Kept flat, as
  // every other list of assets is, so the shared status decoration applies to it unchanged.
  const [attributeValues, setAttributeValues] = useState([]);
  const [defaultSelectedTab, setDefaultSelectedTab] = useState({
    tab: queryParams.tab,
  });
  // Tree or flat list, chosen in the toolbar and remembered between visits. Flat is the mode for
  // scanning: it lists every asset at one level, sorted by name.
  const [assetListMode, setAssetListMode] = useState(getStoredAssetListMode);

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { glossary_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { fetchPrincipals } = usePrincipal();
  const { appendStatus } = useStatus({ setState: setEntities, assetCategory: Asset.Glossary });
  const { notFound, setNotFound, NotFound } = useNotFound();
  const { renderCustomPropertyValues, fetchCustomProperties } = useCustomProperties({
    objectName: Asset.Glossary,
    objectIds: [glossary_id],
  });

  useEffect(() => {
    fetchItems();
    fetchFromReferences();
    fetchEntityTypes();
  }, [glossary_id]);

  useEffect(() => {
    fetchEntities();

    clearSelectedItems();
  }, []);

  // TODO: Improve this to let users edit and delete assets of different types.
  // Current solution is to not allow edit and delete if assets of different types are selected.
  useEffect(() => {
    if (selectedItems.length > 0) {
      let tmpEnableEdit = true;
      let objectName = selectedItems[0]?.object_name;

      for (let i = 0; i < selectedItems.length; i++) {
        if (objectName !== selectedItems[i].object_name) {
          tmpEnableEdit = false;
          break;
        }
      }

      setEnableEdit(tmpEnableEdit);
    } else {
      setEnableEdit(true);
    }
  }, [selectedItems]);

  const fetchItems = async () => {
    glossaryService
      .getById(glossary_id)
      .then(res => {
        if (res.glossaries.length < 1) {
          setNotFound(true);
        } else {
          setGlossary(res.glossaries[0]);
        }
      })
      .catch(err => {
        handleError({ err, showMessage, setNotFound });
      })
      .finally(() => setLoading(false));
  };

  const fetchEntities = async () => {
    await fetchGlossaryAssetRows({ glossaryId: glossary_id })
      .then(({ rows, referenceCounts }) => {
        setEntities(() => rows);
        appendStatus();
        setLoadingTable(false);
        updateReferenceCount(referenceCounts);
        fetchPrincipals(rows, setEntities);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchFromReferences = async () => {
    referenceService
      .getByFromId(Asset.Glossary, glossary_id)
      .then(res => {
        setFromReferences(res.references);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchEntityTypes = async () => {
    //setLoadingEntityTypesTable(true);
    entityTypeService
      .getByGlossaryId(glossary_id)
      .then(res => {
        setEntityTypes(res.entity_types || []);
        setLoadingEntityTypesTable(false);
      })
      .catch(err => {
        showMessage(err, 'error');
        setLoadingEntityTypesTable(false);
      });
  };

  const addEntityType = async data => {
    const props = { entity_types: Array.isArray(data) ? data : [data] };
    try {
      const res = await entityTypeService.create(props).catch(err => {
        throw err;
      });
      setSideDialog(prev => ({ ...prev, formErrors: {} }));
      fetchEntityTypes();
      fetchEntities();
      showMessage(res.message);
    } catch (err) {
      handleError({ err, showMessage, setSideDialog });
      throw err;
    }
  };

  const editEntityType = async data => {
    const props = { entity_types: Array.isArray(data) ? data : [data] };
    try {
      const res = await entityTypeService.update(props).catch(err => {
        throw err;
      });
      fetchEntityTypes();
      fetchEntities();
      showMessage(res.message);
      setSelectedEntityTypes([]);
      hideSideDialog();
    } catch (err) {
      handleError({ err, showMessage, setSideDialog });
      throw err;
    }
  };

  const deleteEntityType = async data => {
    const props = { entity_types: Array.isArray(data) ? data : [data] };
    try {
      const res = await entityTypeService.destroy(props).catch(err => {
        throw err;
      });
      hideModalDialog();
      setSelectedEntityTypes([]);
      fetchEntityTypes();
      fetchEntities();
      showMessage(res.message);
    } catch (err) {
      showMessage(err, 'error');
    }
  };

  const updateReferenceCount = statistics => {
    let statistic = {};
    let maxValue = 0;

    statistics.forEach(row => {
      if (row.to_references_count > maxValue) {
        maxValue = parseInt(row.to_references_count);
      }
    });

    setEntities(prev =>
      prev.map(item => {
        statistic = statistics.find(
          x => x.to_object_id === item.object_id && x.to_object_name === item.object_name
        );
        return {
          ...item,
          to_references_count: statistic ? statistic.to_references_count : 0,
          to_references_count_max: maxValue,
        };
      })
    );
  };

  /**
   * Replaces the loaded values of the given attributes, leaving every other attribute's alone.
   * @param {Array<number|string>|number|string} attributeIds - The attributes just read
   * @param {object[]} values - Their values as just read, in one list
   */
  const mergeValues = (attributeIds, values) => {
    // A set of what is being replaced, so re-reading many attributes is one pass over the state and
    // one render rather than one of each per attribute.
    const ids = new Set([].concat(attributeIds).map(Number));

    setAttributeValues(prev => [
      ...prev.filter(value => !ids.has(Number(value.attribute_id))),
      ...values,
    ]);
    appendStatus({ setState: setAttributeValues, assetCategory: Asset.Glossary });
  };

  /**
   * Loads the values of one attribute, for expanding its row. The promise is returned so the table
   * can show the row as loading until they arrive.
   * @param {object} item - The row being expanded
   * @returns {Promise<void>|undefined} undefined for a row that has no values to load
   */
  const fetchValues = item => {
    if (item.object_name !== Asset.Attribute) return;

    // Already read once. The rows are still here — collapsing a row does not discard them — so
    // re-expanding shows them again without another request. Anything changed from this page re-reads
    // them (refreshValues), which is what keeps them current.
    if (attributeValues.some(value => Number(value.attribute_id) === Number(item.attribute_id))) {
      return;
    }

    return fetchAttributeValues({ attributeIds: [item.attribute_id] })
      .then(values => mergeValues(item.attribute_id, withValuePaths(values, entities)))
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Re-reads the values of the attributes whose expansions are open, after one of them has changed.
   * The tree itself is reloaded separately, which is what refreshes the counts.
   *
   * All of them are read in one request: opening a row keeps its values until the page is left, so
   * what this covers grows as the tree is explored, and a request each would be a request per row
   * ever opened.
   *
   * @param {Array<number|string>} [attributeIds] - Attributes to re-read; defaults to every open one
   */
  const refreshValues = attributeIds => {
    const ids = attributeIds || [...new Set(attributeValues.map(value => value.attribute_id))];

    if (ids.length === 0) return Promise.resolve();

    return fetchAttributeValues({ attributeIds: ids })
      .then(values => mergeValues(ids, withValuePaths(values, entities)))
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Searches the glossary's attribute values server-side, for the table's filter box.
   * @param {string} term - What the user typed
   * @param {{signal: AbortSignal, top: number, fields: string[]}} options - From the DataTable
   * @returns {Promise<object[]>} Matching value rows
   */
  const searchValues = (term, { signal, top, fields }) =>
    searchAttributeValues({
      scope: `glossary_id eq ${Number(glossary_id)}`,
      term,
      fields,
      top,
      signal,
    }).catch(err => {
      showMessage(err, 'error');
      return [];
    });

  const handleAddAsset = objectName => {
    // The glossary asset form works with entities and attributes, and reads the selection to copy
    // from. A selected attribute value is neither, so it is left out and the form opens blank rather
    // than reading the value's own attribute as the asset being copied.
    const assetSelection = selectedItems.filter(item => item.object_name !== Asset.AttributeValue);

    switch (objectName) {
      case Asset.Entity:
        showSideDialog('addEntity', assetSelection, addAsset);
        break;
      case 'AddChildEntity':
        showSideDialog('addChildEntity', assetSelection, addAsset);
        break;
      case 'AddChildAttributeValue':
        // The selected row is the attribute the value belongs to, not something to copy, so only its
        // id is carried over — exactly as adding a value from the attribute list does.
        showSideDialog(
          'addAttributeValue',
          [
            {
              status_id: statuses.default_glossary_status_id,
              attribute_id: selectedItems[0].attribute_id,
            },
          ],
          addValue
        );
        break;
      case Asset.Attribute:
        // The selection is passed for context only — the form reads it for the glossary and, when
        // adding a child, the parent. It must not be seeded with the selected asset's own name or
        // description: this used to copy them onto the new asset, and did so by overwriting
        // selectedItems[0] in place, which corrupted the view's selection too.
        showSideDialog('addAttribute', assetSelection, addAsset);
        break;
      default:
      // nothing
    }
  };

  const handleEditAsset = items => {
    items = items || selectedItems;

    const objectName = getObjectNameFromSelection(items);

    switch (objectName) {
      case Asset.Entity:
        showSideDialog('editEntity', items, editAsset);
        break;
      case Asset.Attribute:
        showSideDialog('editAttribute', items, editAsset);
        break;
      case Asset.AttributeValue:
        showSideDialog('editAttributeValue', items, editValue);
        break;
      case 'mixed':
        // Not supported, do nothing
        break;
      default:
      // nothing
    }
  };

  const handleDeleteAsset = async items => {
    items = items || selectedItems;

    const objectName = getObjectNameFromSelection(items);

    switch (objectName) {
      case Asset.Entity:
        showModalDialog('deleteEntity', items, data => deleteAsset(data, Asset.Entity));
        break;
      case Asset.Attribute:
        showModalDialog('deleteAttribute', items, data => deleteAsset(data, Asset.Attribute));
        break;
      case Asset.AttributeValue:
        showModalDialog('deleteAttributeValue', items, data =>
          deleteAsset(data, Asset.AttributeValue)
        );
        break;
      case 'mixed':
        // Not supported, do nothing
        break;
      default:
      // nothing
    }
  };

  const addAsset = async data => {
    // Which kind is being created comes from the role the user picked, not from the button they
    // pressed to open the form.
    const { service, payload } = resolveGlossaryAssetSubmit(data, 'add');

    try {
      const res = await service.create(payload).catch(err => {
        throw err;
      });

      setSideDialog(prev => ({ ...prev, formErrors: {} }));
      fetchEntities();
      showMessage(res.message);
    } catch (err) {
      handleError({ err, showMessage, setSideDialog });
      throw err;
    }
  };

  /**
   * Creates one or more attribute values under an attribute in the tree.
   * @param {object|object[]} data - The value(s) to create
   */
  const addValue = async data => {
    const rows = Array.isArray(data) ? data : [data];

    try {
      const res = await attributeValueService.create({ attribute_values: rows }).catch(err => {
        throw err;
      });

      setSideDialog(prev => ({ ...prev, formErrors: {} }));
      // The tree carries the value counts, so it is reloaded alongside the values themselves.
      fetchEntities();
      refreshValues([...new Set(rows.map(row => row.attribute_id))]);
      showMessage(res.message);
    } catch (err) {
      handleError({ err, showMessage, setSideDialog });
      throw err;
    }
  };

  /**
   * Updates one or more attribute values.
   * @param {object|object[]} data - The value(s) to update
   */
  const editValue = async data => {
    const rows = Array.isArray(data) ? data : [data];

    try {
      const res = await attributeValueService.update({ attribute_values: rows }).catch(err => {
        throw err;
      });

      // An edit can move a value to another attribute, so both the attribute it left and the one it
      // arrived at are re-read — which is every open expansion, plus any newly named attribute.
      refreshValues([
        ...new Set([
          ...attributeValues.map(value => value.attribute_id),
          ...rows.map(row => row.attribute_id),
        ]),
      ]);
      fetchEntities();
      showMessage(res.message);
      clearSelectedItems();
      hideSideDialog();
    } catch (err) {
      handleError({ err, showMessage, setSideDialog });
      throw err;
    }
  };

  const editGlossary = async data => {
    const glossaries = { glossaries: Array.isArray(data) ? data : [data] };

    await glossaryService
      .update(glossaries)
      .then(async res => {
        fetchItems();
        fetchCustomProperties(false);
        fetchFromReferences();
        showMessage(res.message);
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editAsset = async data => {
    // Changing the role across the Term/Dimension boundary moves the asset to the other resource, so
    // that arrives here as a create against the new kind rather than an update.
    const { service, payload, isCreate } = resolveGlossaryAssetSubmit(data, 'edit');

    try {
      const res = await (isCreate ? service.create(payload) : service.update(payload)).catch(
        err => {
          throw err;
        }
      );

      fetchEntities();
      // Saving an attribute can add, change and delete its values, which hang under it here as rows
      // of their own. The reload above refreshes the counts; the values themselves are held per
      // attribute and are re-read for the rows that are open.
      refreshValues();
      showMessage(res.message);
      clearSelectedItems();
      hideSideDialog();
    } catch (err) {
      handleError({ err, showMessage, setSideDialog });
      throw err;
    }
  };

  const deleteAsset = async (data, objectName) => {
    let service;

    switch (objectName) {
      case Asset.Entity:
        service = entityService;
        const items = Array.isArray(data.items) ? data.items : [data.items];
        data = { entities: items, deleteChildren: data.deleteChildren };
        break;
      case Asset.Attribute:
        service = attributeService;
        data = Array.isArray(data) ? data : [data];
        data = { attributes: data };
        break;
      case Asset.AttributeValue:
        service = attributeValueService;
        data = Array.isArray(data) ? data : [data];
        data = { attribute_values: data };
        break;
      default:
      // nothing
    }

    try {
      const res = await service.destroy(data).catch(err => {
        throw err;
      });

      hideModalDialog();
      clearSelectedItems();
      fetchEntities();
      // A deleted value leaves an open expansion showing it until its attribute is re-read.
      if (objectName === Asset.AttributeValue) refreshValues();
      showMessage(res.message);
    } catch (err) {
      showMessage(err, 'error');
    }
  };

  const deleteGlossary = async data => {
    await glossaryService
      .destroy({ glossaries: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();

        if (location.key !== 'default') {
          // Arrived here by navigating inside Katalogue, so return the user where they came from.
          navigate(-1);
        } else {
          // Landed directly on this page — a deep link, a refresh or a new tab — so there is no
          // history to return to. Fall back to the deleted asset's parent.
          navigate('/glossaries');
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Builds the "Add Child" button for the selected row: an asset under an entity, or a value under an
   * attribute. Only a Dimension can hold values, so for a Measure the button is shown but disabled
   * with the reason in its tooltip — the value form's own attribute picker offers only Dimensions,
   * but a pre-selected attribute never passes through it.
   * @returns {object} A DataTable button definition, or {} when the selection cannot have children
   */
  const getAddChildButton = () => {
    if (selectedItems.length !== 1) return {};

    if (selectedItems[0].object_name === Asset.Entity) {
      return {
        label: 'Add Child',
        tooltip: 'Add a new asset with the currently selected entity as parent',
        onClick: () => handleAddAsset('AddChildEntity'),
      };
    }

    if (selectedItems[0].object_name === Asset.Attribute) {
      const allowed = canHaveAttributeValues(selectedItems[0]);
      return {
        label: 'Add Child',
        disabled: !allowed,
        tooltip: allowed
          ? 'Add an attribute value to the currently selected attribute'
          : "Attribute Values can only be registered on Attributes with role 'Dimension'",
        onClick: () => handleAddAsset('AddChildAttributeValue'),
      };
    }

    return {};
  };

  // The tree: entities and attributes, plus the values of the attributes that have been expanded.
  // The values are rows of the same table, placed under their attribute by parent_unique_id, which
  // is what keeps their checkbox and their columns in line with everything above them.
  const assetRows = useMemo(() => [...entities, ...attributeValues], [entities, attributeValues]);

  const isFlatAssetList = assetListMode === ASSET_LIST_MODE.flat;

  // What the table is given: the tree as it stands, or the same rows flattened.
  const assetTableRows = useMemo(
    () => (isFlatAssetList ? toFlatAssetRows(assetRows) : assetRows),
    [isFlatAssetList, assetRows]
  );

  const data = loading ? [] : glossary;

  const editableGlossary = loggedInUser && loggedInUser.user_role_name === Role.admin;
  const editableEntity =
    loggedInUser &&
    (loggedInUser.user_role_name === Role.admin || loggedInUser.user_role_name === Role.editor);

  if (notFound) {
    return <NotFound />;
  }

  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type="Glossary"
          statusId={data.status_id}
          title={data.glossary_name}
          subtitleText={data && !loading && data.glossary_type}
          description={data.glossary_description}
          details={[
            ...renderCustomPropertyValues().details,
            {
              title: 'Last Modified',
              data: formatData(data.modified_timestamp, 'datetime'),
            },
            {
              title: 'Created',
              data: formatData(data.created_timestamp, 'datetime'),
            },
            {
              title: 'Id',
              data: data.glossary_id,
              tooltip: 'Katalogue-internal id for this asset',
            },
          ]}
          fromReferences={fromReferences}
          loading={loading}
          editable={editableGlossary}
          buttons={[
            {
              buttonStyle: 'main-button',
              value: 'Edit',
              onClick: () => showSideDialog('editGlossary', [data], editGlossary),
            },
            {
              value: 'Delete',
              onClick: () => showModalDialog('deleteGlossary', [data], deleteGlossary),
            },
          ]}
        >
          <ObjectDetail label="Owner" loading={loading}>
            <PrincipalLink principalId={data.owner_principal_id} loading={loading} />
          </ObjectDetail>

          {renderCustomPropertyValues().main}
        </ObjectDetails>
      </MainColumn>

      <div className="column">
        <Tabs
          className="slim left"
          //onTabChange={ onTabChange }
          defaultSelectedTab={defaultSelectedTab}
        >
          <div
            label="Assets"
            tabid="assets"
            resultCount={loadingTable || entities.length === 0 ? undefined : entities.length}
          >
            <DataTable
              columns={[
                {
                  id: 'entity_name',
                  name: 'Name',
                  link: ':link',
                  tooltip: 'entity_description',
                  tooltipSubtitle: 'entity_path',
                  className: ':className',
                  // Every row carries its role — Entity, Dimension or Measure — and shows the same
                  // icon the asset form's role picker uses for it.
                  inline: [
                    { type: 'icon', icon: 'attribute_role_name' },
                    // A flat row has lost the nesting that said where it sits, so the assets above
                    // it are named on a second line under it. It reads as part of the name rather
                    // than as a property of the row, so it takes no header slot of its own
                    // (sortable: false) — the name's own sort orders these rows.
                    ...(isFlatAssetList
                      ? [
                          {
                            type: 'text',
                            value: 'entity_parent_path',
                            className: 'primary-column-text-block',
                            after: true,
                            block: true,
                            sortable: false,
                          },
                        ]
                      : []),
                  ],
                },
                {
                  id: 'entity_description',
                  name: 'Description',
                  type: 'description',
                  viewMode: 'descriptions',
                },
                {
                  id: 'entity_type_name',
                  name: 'Type',
                  viewMode: 'statistics',
                  className: 'small-text',
                  tooltip: 'entity_type_description',
                },
                {
                  id: 'to_references_count',
                  name: 'Related Assets',
                  type: 'chart',
                  // References to a value are not counted with the tree's, so the cell is left blank
                  // rather than reading "-", which would say the number is unknown.
                  appliesTo: [Asset.Entity, Asset.Attribute],
                  chartType: 'bar',
                  data: {
                    data: 'to_references_count',
                    max: 'to_references_count_max',
                  },
                  viewMode: 'statistics',
                },
                {
                  id: 'pii',
                  name: '',
                  type: 'icon',
                  tooltip: 'pii_tooltip',
                  className: 'no-right-margin',
                },
                {
                  id: 'attribute_sensitivity_name',
                  name: 'Data Sensitivity',
                  type: 'pill',
                  value: 'attribute_sensitivity_level',
                  values: AttributeSensitivity,
                  tooltip: 'attribute_sensitivity_name',
                },
                //{id: 'modified_timestamp', name: 'Modified', type: 'datetime', viewMode: 'statistics'},
                ...(settings.SHOW_GLOSSARY_STATUSES
                  ? [
                      {
                        id: 'status_name',
                        name: 'Status',
                        type: 'pill',
                        value: 'status_id',
                        values: statuses,
                        tooltip: 'status_description',
                      },
                    ]
                  : []),
                {
                  id: 'principal_name',
                  name: 'Owner',
                  type: 'owner',
                  // An owner belongs to the asset itself: a value inherits its parent's, and showing it
                  // again on every row would read as the value having an owner of its own.
                  appliesTo: [Asset.Entity, Asset.Attribute],
                  link: '/:principal_url',
                  data: { photo: 'principal_photo' },
                  tooltipSubtitle: 'principal_type_name',
                },
              ]}
              buttons={[
                {
                  label: '+ Add',
                  action: 'add',
                  mainButton: true,
                  // One button, because the form's role picker is what chooses between an entity and
                  // an attribute. It opens on Entity; the user can change the role there.
                  tooltip: 'Add a glossary asset',
                  onClick: () => handleAddAsset(Asset.Entity),
                },

                getAddChildButton(),
                {
                  label: 'Edit',
                  action: 'edit',
                  disabled: !enableEdit,
                  tooltip:
                    !enableEdit &&
                    'Assets of different types have been selected. Make sure to only select assets of the same type.',
                  onClick: items => handleEditAsset(items),
                },
                {
                  label: 'Delete',
                  action: 'delete',
                  disabled: !enableEdit,
                  tooltip:
                    !enableEdit &&
                    'Assets of different types have been selected. Make sure to only select assets of the same type.',
                  onClick: items => handleDeleteAsset(items),
                },
              ]}
              data={assetTableRows}
              idColumn="unique_id"
              primaryColumn="entity_name"
              parentIdColumn="parent_unique_id"
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              loading={loadingTable}
              editable={editableEntity}
              filterable={true}
              filterObjects={[
                'entity_name',
                'entity_description',
                // Both kinds share this table, so the role — Entity, Dimension or Measure — and the
                // entity type are worth filtering on: "entity" alone, or the entities of one type.
                'attribute_role_name',
                'entity_type_name',
                'is_pii',
                'attribute_sensitivity_name',
                'attribute_value_code',
                ...(settings.SHOW_GLOSSARY_STATUSES ? ['status_name'] : []),
                'principal_name',
              ]}
              filterControls={[
                {
                  type: 'select-buttons',
                  design: 'bright',
                  name: ASSET_LIST_MODE_KEY,
                  tooltip: 'Show the assets as a tree or as a flat list',
                  saveInLocalStorage: true,
                  defaultValue: assetListMode,
                  options: [
                    {
                      value: ASSET_LIST_MODE.tree,
                      icon: 'tree',
                      tooltip: 'Tree — show every asset under the asset it belongs to',
                    },
                    {
                      value: ASSET_LIST_MODE.flat,
                      icon: 'flat',
                      tooltip: 'Flat list — show every asset at one level, sorted by name',
                    },
                  ],
                  onChange: value => setAssetListMode(value[ASSET_LIST_MODE_KEY]),
                },
              ]}
              filterControlsPosition="right"
              // A flat list is sorted by name; the tree keeps the order it is built in, where a sort
              // could only order siblings within their parent.
              defaultSort={isFlatAssetList ? { key: 'entity_name', direction: 'asc' } : undefined}
              showViewModes={true}
              expandable={true}
              expandableIndentIcons={true}
              // An attribute's values are read when it is expanded, so the count is what tells the
              // table an attribute has any before they exist.
              childCountColumn="attribute_value_count"
              onExpand={fetchValues}
              childFilter={{ fetch: searchValues }}
            />
          </div>

          {loggedInUser.user_role_name === Role.admin && (
            <div
              label="Entity Types"
              tabid="entity_types"
              resultCount={
                loadingEntityTypesTable || entityTypes.length === 0 ? undefined : entityTypes.length
              }
            >
              <DataTable
                columns={[
                  {
                    id: 'entity_type_name',
                    name: 'Name',
                    className: 'bold',
                  },
                  {
                    id: 'entity_type_code',
                    name: 'Code',
                  },
                  {
                    id: 'entity_type_description',
                    name: 'Description',
                    type: 'description',
                    viewMode: 'descriptions',
                  },
                  {
                    id: 'entity_type_is_default',
                    name: 'Is Default',
                    type: 'boolean',
                    values: {
                      true: { label: 'Yes', className: 'pill pill-black' },
                      false: { label: 'No', className: 'pill pill-grey' },
                    },
                  },
                  {
                    id: 'modified_timestamp',
                    name: 'Modified',
                    type: 'datetime',
                  },
                ]}
                buttons={[
                  {
                    label: '+ Add',
                    action: 'add',
                    mainButton: true,
                    onClick: () => {
                      showSideDialog(
                        'addEntityType',
                        [{ glossary_id: parseInt(glossary_id) }],
                        addEntityType
                      );
                    },
                  },
                  {
                    label: 'Edit',
                    action: 'edit',
                    onClick: items => {
                      showSideDialog(
                        'editEntityType',
                        items || selectedEntityTypes,
                        editEntityType
                      );
                    },
                  },
                  {
                    label: 'Delete',
                    action: 'delete',
                    onClick: items =>
                      showModalDialog(
                        'deleteEntityType',
                        items || selectedEntityTypes,
                        deleteEntityType
                      ),
                  },
                ]}
                data={entityTypes}
                idColumn="entity_type_id"
                selectedItems={selectedEntityTypes}
                setSelectedItems={setSelectedEntityTypes}
                loading={loadingEntityTypesTable}
                editable={editableEntity}
                filterable={true}
                filterObjects={['entity_type_name', 'entity_type_code', 'entity_type_description']}
                showViewModes={true}
              />
            </div>
          )}

          <div label="Changelog" tabid="changelog">
            <Changelog
              objectName={Asset.Glossary}
              objectId={glossary_id}
              filterObjects={[
                'changedData.new_data||old_data.glossary_name',
                'changedData.new_data||old_data.glossary_type',
                'changedData.new_data||old_data.glossary_description',
                'changedData.new_data||old_data.principal_name',
                'changedData.property',
                'changed_by_user_username',
                'is_service_account_user',
              ]}
              filterObjectsValueMap={{
                is_service_account_user: {
                  true: { label: 'Datasource Sync' },
                  false: { label: '' },
                },
              }}
            />
          </div>
        </Tabs>
      </div>
    </div>
  );
};
